<template>
  <ContainerWrapper
    :title="pageTitle"
    :no-white-container="isNewForm"
  >
    <component
      :is="dynamicInvoiceFormComponent"
      ref="invoiceForm"
      :is-editable="!editDisabled"
      :is-edit-eet-invoice-disabled="editModeEetInvoice"
      :correction-invoice-redirect="correctionInvoiceRedirect"
    />
  </ContainerWrapper>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { NEW_INVOICE, EDIT_INVOICE, COUNTRY_CODES } from '~/config/constants';
import {
  MSG_INVOICE_EET_EDIT_BLOCKED,
  MSG_INVOICE_EET_REGISTER,
} from '~/config/flashMessagesConstants';
import { PATH_INVOICE_CREDIT_NOTE, PATH_INVOICES_OVERVIEW } from '~/router';
import { GEO_INFO_QUERY } from '~/queries';

export default {
  name: 'InvoicePage',
  inject: {
    invoiceKind: {
      default: null,
    },
  },
  components: {
    InvoiceForm: () => import('~/components/InvoiceForm/InvoiceForm.vue'),
    InvoiceFormV2: () => import('~/components/v2/InvoiceForm/InvoiceForm.vue'),
    InvoiceFormV3: () => import('~/components/v3/InvoiceForm/InvoiceForm.vue'),
  },
  props: {
    invoiceType: String,
  },
  provide() {
    return {
      invoiceType: this.invoiceType,
    };
  },
  data: () => ({
    skipIpInfoQuery: true,
    formVersion: 'v1',
  }),
  apollo: {
    ipInfo: {
      query: GEO_INFO_QUERY,
      skip() {
        return this.skipIpInfoQuery;
      },
      update({ ipInfo }) {
        const { countryCode, currencyCode, language } = ipInfo;
        if (currencyCode) {
          this.$store.dispatch('invoiceForm/updateStoreValue', {
            name: 'currency',
            value: currencyCode,
          });
        }

        if (language) {
          const languageFromIpInfo = this.languageOptions.find(option => option.key === language)?.key;

          if (languageFromIpInfo) {
            this.$store.dispatch('invoiceForm/updateStoreValue', {
              name: 'language',
              value: languageFromIpInfo,
            });
          }
        }

        if (countryCode) {
          this.$store.dispatch('invoiceForm/updateStoreValue', {
            name: 'seller_address_country_code',
            value: countryCode,
          });

          this.$store.dispatch('invoiceForm/updateStoreValue', {
            name: 'buyer_address_country_code',
            value: countryCode,
          });
        }
      },
    },
  },
  computed: {
    invoiceId() {
      return this.$route.params.invoiceId;
    },
    ...mapGetters('auth', ['isAuthorized']),
    ...mapGetters('invoiceForm', [
      'shouldBeRegistered',
      'isLocked',
      'buildComplete',
    ]),
    ...mapState('invoiceForm', ['eet_invoice', 'number']),
    ...mapState('config', {
      languageOptions: state => state.invoice.languages,
    }),
    breadcrumbs() {
      const crumb = [
        {
          key: 'invoices',
          link: PATH_INVOICES_OVERVIEW,
        },
      ];

      if (this.isNewInvoice) {
        crumb.push({ key: `${this.invoiceType}Invoice` });
      }

      return crumb;
    },
    correctionInvoiceRedirect() {
      return () => this.$router.push({
        name: PATH_INVOICE_CREDIT_NOTE,
        params: { invoiceId: this.invoiceId },
      });
    },
    editModeEetInvoice() {
      if (this.invoiceType === EDIT_INVOICE && this.isLocked) {
        this.$store.dispatch(
          'flashMessages/addMessage',
          {
            ...MSG_INVOICE_EET_EDIT_BLOCKED,
            button: {
              ...MSG_INVOICE_EET_EDIT_BLOCKED.button,
              action: this.correctionInvoiceRedirect,
            },
          },
          { root: true },
        );
        return true;
      }
      if (this.invoiceType === EDIT_INVOICE && this.shouldBeRegistered) {
        this.$store.dispatch(
          'flashMessages/addMessage',
          MSG_INVOICE_EET_REGISTER,
          { root: true },
        );
        return false;
      }
      return false;
    },
    isNewInvoice() {
      return this.invoiceType === NEW_INVOICE;
    },
    editDisabled() {
      return this.editModeEetInvoice === true;
    },
    pageTitle() {
      if (this.invoiceKind && this.isInvoiceKindRoute) {
        return this.$i18n.t(`form.invoiceForm.headers.${this.invoiceType}_${this.invoiceKind}_title`);
      }
      return this.$i18n.t(`form.invoiceForm.headers.${this.invoiceType}_title`);
    },
    dynamicInvoiceFormComponent() {
      switch (this.formVersion) {
        case 'v2':
          return 'InvoiceFormV2';
        case 'v3':
          return 'InvoiceFormV3';
        default:
          return 'InvoiceForm';
      }
    },
    isInvoiceKindRoute() {
      return this.$route.name === 'invoiceKind';
    },
    isNewForm() {
      return this.formVersion === 'v2' || this.formVersion === 'v3';
    },
  },
  methods: {
    setFormVersion() {
      this.$posthog?.onFeatureFlags(() => {
        const phVersion = this.$posthog?.getFeatureFlag('invoice-form-version');

        if (phVersion === 'control') {
          this.formVersion = 'v2';
        } else if (phVersion === 'minified') {
          this.formVersion = 'v3';
        }
      });

      const version = this.$route.query.form_version;

      if (['v1', 'v2', 'v3'].includes(version)) {
        this.formVersion = version;
      }
    },
    checkInvoiceTypeFromParams() {
      setTimeout(() => {
        if (this.isInvoiceKindRoute && this.invoiceKind != null) {
          this.$store.dispatch('invoiceForm/updateStoreValue', {
            name: 'kind',
            value: this.invoiceKind,
          });
        }
      }, 500);
    },
    refreshData() {
      if (this.invoiceType !== NEW_INVOICE) {
        const attributes = {
          invoiceId: this.invoiceId,
          type: this.invoiceType,
        };

        this.$store.dispatch('invoiceForm/buildInvoice', attributes);
      } else {
        this.$store.dispatch('invoices/getNewInvoice');
        this.checkInvoiceTypeFromParams();
        this.fetchGeoInfo();
      }
    },
    checkLinesDialgFromParams() {
      if (typeof this.$route.query.showLines !== 'undefined') {
        this.$store.dispatch('savedLines/openDialog');
        this.$store.dispatch('savedLines/getCollection', { page: 1 });
      }
    },
    fetchGeoInfo() {
      const country = COUNTRY_CODES[this.$i18n.locale.toUpperCase()];
      const canFetchIp = (country === COUNTRY_CODES.EN || country === COUNTRY_CODES.RS);
      if (!this.isAuthorized && canFetchIp) {
        this.skipIpInfoQuery = false;
        this.$apollo.queries.ipInfo.refetch();
      }
    },
    setRoute() {
      if (this.number && !this.isNewInvoice) {
        this.$store.dispatch(
          'breadcrumbs/setBreadcrumbs',
          [
            {
              key: 'invoices',
              link: PATH_INVOICES_OVERVIEW,
            },
            {
              key: 'invoice',
              arg: this.number,
            }],
        );
      }
    },
    updateFromParams() {
      this.checkInvoiceTypeFromParams();

      if (this.$route.params.lines) {
        this.$route.params.lines.forEach((line) => {
          this.$store.dispatch('invoiceForm/addInvoiceItemWithData', line);
        });
      }

      if (typeof this.$route.query.contactId !== 'undefined') {
        this.$store.dispatch('invoiceForm/updateStoreValue', {
          name: 'buyer_company_name',
          value: parseInt(this.$route.query.contactId, 10),
        });

        this.$store.dispatch(
          'invoiceForm/fetchContactData',
          this.$route.query.contactId,
        );
      }
    },
    updateBodyClass() {
      if (this.isNewForm) {
        document.body.classList.add('new-form-enabled');
      } else {
        document.body.classList.remove('new-form-enabled');
      }
    },
  },
  watch: {
    $route() {
      this.$store.dispatch('invoiceForm/resetState');
      this.$store.dispatch('invoiceForm/setDefaultKind');
      this.refreshData();
    },
    buildComplete() {
      this.checkLinesDialgFromParams();
      this.setRoute();
    },
    invoiceType() {
      this.$store.dispatch('flashMessages/clearMessage');
    },
    isNewForm() {
      this.updateBodyClass();
    },
  },
  mounted() {
    this.refreshData();
    this.setFormVersion();

    const unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'invoiceForm/setInvoiceData') {
        this.updateFromParams();
        unsubscribe();
      }
    });
  },
  beforeDestroy() {
    this.$store.dispatch('invoiceForm/resetState');
  },
};
</script>
